import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Subscription } from '../../../../common/src/model.ts';
import { naturalStringCompare } from '../../../../common/src/util.ts';
import {
  LocalStorageKey,
  clearClientLocalStorage,
  getAllLocalStorage,
  getAllLocalStorageKeys,
  keyChanged,
} from '../../data/client_local_storage.ts';
import { isInsideAppleAppStoreApp, requestIOSAppVersion } from '../../util/billing.ts';
import { alert, confirm } from '../../util/confirm.tsx';
import '../settings_page.css';

export const LocalStoragePage = () => (
  <LocalStorageWrapper>
    <LocalStorageTable />
  </LocalStorageWrapper>
);

const LocalStorageWrapper = styled.div`
  background-color: var(--color-background);
  height: 100%;
  overflow: auto;
`;

const ButtonContainer = styled.div`
  display: flex;
`;

const LocalStorageButton = styled.button`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1.2em;
  border-radius: 15px;
  padding: 2px 12px;
  margin: 20px;
  border: none;
  color: white;
  cursor: pointer;
`;

const ClearAllButton = styled(LocalStorageButton)`
  background-color: var(--color-dangerous-button);
`;

const CopyAllButton = styled(LocalStorageButton)`
  background-color: var(--color-friendly-button);
`;

const LocalStorageTable = () => {
  const [refresh, setRefresh] = React.useState('');

  const clearAllSettings = useCallback(async () => {
    if (await confirm({ confirmation: <span>Delete all keys?</span> })) {
      const keys = getAllLocalStorageKeys();
      clearClientLocalStorage();
      if (isInsideAppleAppStoreApp()) {
        requestIOSAppVersion();
      }
      for (const key of keys) {
        keyChanged(key);
      }
      setRefresh(refresh + '1'); // refresh page
    }
  }, [refresh]);

  const copyAllSettings = useCallback(async () => {
    const localStorage = Object.fromEntries(Object.entries(getAllLocalStorage()).filter(([key]) => key !== 'logging'));
    const result = JSON.stringify(localStorage, null, 2);
    await navigator.clipboard.writeText(result);
    await alert({ confirmation: <span>Copied to clipboard</span> });
  }, []);

  return (
    <ScrollContainer>
      <ButtonContainer>
        <ClearAllButton onClick={clearAllSettings}>Clear All</ClearAllButton>
        <CopyAllButton onClick={copyAllSettings}>Copy</CopyAllButton>
      </ButtonContainer>
      <LocalStorageTableStyles>
        {Object.keys(localStorage)
          .sort((lhs, rhs) => naturalStringCompare(lhs, rhs))
          .map((key) => (
            <LocalStorageRow key={key}>
              <Cell key={key + '0'} className="localStorageKey">
                {key}
              </Cell>
              <Value key={key + '1'} value={formatLocalStorageValue(key, localStorage[key])} />
              <DeleteButton key={key + '2'} localStorageKey={key} refresh={() => setRefresh(key)} />
            </LocalStorageRow>
          ))}
      </LocalStorageTableStyles>
    </ScrollContainer>
  );
};

function Value({ value }: { value: string }) {
  const [expanded, setExpanded] = useState(false);
  const onClick = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);
  return (
    <ValueWrapper $expanded={expanded} onClick={onClick}>
      {value}
    </ValueWrapper>
  );
}

function formatLocalStorageValue(key: string, value: string): string {
  let formattedValue = value;
  try {
    let object = JSON.parse(value);
    if (key === LocalStorageKey.ClientSubscription) {
      const subscription = object as Subscription;
      object = {
        ...object,
        ...(subscription.subscriptionExpirationTimestamp && {
          expirationDate: new Date(subscription.subscriptionExpirationTimestamp).toString(),
          inFuture: Boolean(new Date(subscription.subscriptionExpirationTimestamp).getTime() > Date.now()),
        }),
      };
    }
    formattedValue = JSON.stringify(object, null, 2);
  } catch {}
  return formattedValue;
}

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;
  height: 100%;
  overflow-y: auto;
`;

async function deleteLocalStorageKey(key: string, refresh: () => void) {
  if (
    await confirm({
      confirmation: (
        <span>
          Delete key?
          <br />
          {key}
        </span>
      ),
    })
  ) {
    localStorage.removeItem(key);
    keyChanged(key);
    refresh();
  }
}

const DeleteButton = ({ localStorageKey, refresh }: { localStorageKey: string; refresh: () => void }) => (
  <DeleteButtonStyles onClick={() => void deleteLocalStorageKey(localStorageKey, refresh)}>X</DeleteButtonStyles>
);

const DeleteButtonStyles = styled.button`
  font-family: Jost-SemiBold, sans-serif;
  font-size: 1em;
  color: var(--color-text-button);
  background-color: red;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  padding: 5px 10px;
  height: 100%;
  margin: auto 0;
`;

const LocalStorageTableStyles = styled.div`
  display: grid;
  grid-template-columns: 35% 1fr auto;
  gap: 5px 5px;
  background-color: var(--color-background);
  padding: 5px;
`;

const LocalStorageRow = styled.div`
  display: contents;
  &:nth-of-type(odd) div {
    background-color: var(--color-background-table-banding);
  }
`;

const Cell = styled.div`
  text-align: left;
  padding: 5px 0;
  white-space: pre-wrap; /* respect spaces & newlines in JSON.stringify output */
`;

const ValueWrapper = styled.div<{ $expanded: boolean }>`
  text-align: left;
  padding: 5px 0;
  ${(props) =>
    props.$expanded
      ? `
      white-space: pre-wrap;
    `
      : `
      white-space: nowrap; /* respect spaces & newlines in JSON.stringify output */
      overflow: hidden;
      text-overflow: ellipsis;
    `}
  cursor: pointer;
`;
